import { ConfigData, CountdownPosition } from '@/types';

export type TimerTemplateId =
	| 'standard'
	| 'black_friday'
	| 'cyber_monday'
	| 'holiday_special'
	| 'flash_sale'
	| 'free_shipping';

type TimerTemplatePatch = Pick<
	ConfigData,
	| 'title'
	| 'subtitle'
	| 'font'
	| 'background_color'
	| 'timer_window_color'
	| 'countdown_color'
	| 'time_units_color'
	| 'title_font_style'
	| 'subtitle_font_style'
	| 'button_bg_color'
	| 'button_text_color'
>;

type TimerTemplateBackground = {
	/** File in `server/src/public/default_backgrounds/`, served under `/api/public`. */
	fileName: string;
	/** Shown by the background picker the same way as the name of an uploaded file. */
	displayName: string;
};

export type TimerTemplate = {
	id: TimerTemplateId;
	name: string;
	patch: TimerTemplatePatch;
	background?: TimerTemplateBackground;
};

export const DEFAULT_TEMPLATE_ID: TimerTemplateId = 'standard';

const TEMPLATE_BACKGROUND_DIRECTORY = '/public/default_backgrounds/';

export const TIMER_TEMPLATES: TimerTemplate[] = [
	{
		id: 'standard',
		name: 'Standard',
		patch: {
			title: "Don't miss out!",
			subtitle: 'Last days to grab amazing deals before the sale ends!',
			background_color: '#0C1839',
			timer_window_color: '#172B61',
			countdown_color: '#D1FF53',
			time_units_color: '#A1B2D9',
			title_font_style: '24px bold #4FB0FF',
			subtitle_font_style: '16px normal #A1B2D9',
			button_bg_color: '#D1FF53',
			button_text_color: '16px bold #0C1839',
			font: 'inter',
		},
	},
	{
		id: 'black_friday',
		name: 'Black Friday',
		background: { fileName: 'black_friday.png', displayName: 'Black Friday.png' },
		patch: {
			title: 'Black Friday sale ends soon',
			subtitle: 'Don’t miss the biggest deals of the year!',
			background_color: '#161616',
			timer_window_color: '#111111',
			countdown_color: '#FFE817',
			time_units_color: '#A7A0B1',
			title_font_style: '24px bold #FFE817',
			subtitle_font_style: '16px bold #FFFFFF',
			button_bg_color: '#FFE817',
			button_text_color: '16px bold #111111',
			font: 'inter',
		},
	},
	{
		id: 'cyber_monday',
		name: 'Cyber Monday',
		background: { fileName: 'cyber_monday.png', displayName: 'Cyber Monday.png' },
		patch: {
			title: 'Cyber Monday',
			subtitle: 'Limited-time online deals - shop now!',
			background_color: '#042442',
			timer_window_color: '#042442',
			countdown_color: '#55FFF3',
			time_units_color: '#00A99A',
			title_font_style: '24px bold #55FFF3',
			subtitle_font_style: '16px normal #FFFFFF',
			button_bg_color: '#55FFF3',
			button_text_color: '16px bold #042442',
			font: 'aldrich',
		},
	},
	{
		id: 'holiday_special',
		name: 'Holiday Special',
		background: { fileName: 'holiday_special.png', displayName: 'Holiday Special.png' },
		patch: {
			title: 'Holiday special offers',
			subtitle: 'Celebrate with exclusive seasonal discounts',
			background_color: '#F2D5E8',
			timer_window_color: '#11142A',
			countdown_color: '#FFFFFF',
			time_units_color: '#11142A',
			title_font_style: '24px bolditalic #11142A',
			subtitle_font_style: '16px normal #11142A',
			button_bg_color: '#11142A',
			button_text_color: '16px bold #FFFFFF',
			font: 'playfair_display',
		},
	},
	{
		id: 'flash_sale',
		name: 'Flash Sale',
		patch: {
			title: 'Flash Sale live now',
			subtitle: 'Grab your favorites before time runs out!',
			background_color: '#B73725',
			timer_window_color: '#B73725',
			countdown_color: '#FFF083',
			time_units_color: '#FFF28C',
			title_font_style: '24px bold #FFF083',
			subtitle_font_style: '16px normal #FFFFFF',
			button_bg_color: '#FFF083',
			button_text_color: '16px bold #B73725',
			font: 'roboto',
		},
	},
	{
		id: 'free_shipping',
		name: 'Free Shipping',
		patch: {
			title: 'Free Shipping promo',
			subtitle: 'Enjoy free delivery before the timer hits zero',
			background_color: '#B8D5F4',
			timer_window_color: '#FFFFFF',
			countdown_color: '#0037E6',
			time_units_color: '#0029E1',
			title_font_style: '24px bold #000000',
			subtitle_font_style: '16px normal #000000',
			button_bg_color: '#0037E6',
			button_text_color: '16px bold #FFFFFF',
			font: 'lato',
		},
	},
];

/**
 * Template looks that the color and font settings cannot express. Mirrors
 * `server/src/embedded-script/services/templateStyles.js` — the storefront script is a separate build. The mobile
 * rules of the bar sit under `.amasty-timer-mobile` here and in `@media (max-width: 768px)` there: the preview is not
 * a narrow viewport. The product container shrinks by its column width, so its `@container` rules are the same in both.
 */
const TEMPLATE_STYLES: Partial<Record<TimerTemplateId, string>> = {
	black_friday: `
		#timer .time-unit .number {
			border-radius: 0 !important;
			margin-bottom: 4px !important;
			padding: 0 4px !important;
		}
	`,
	cyber_monday: `
		#timer .time-unit .number {
			border-radius: 0;
		}

		#timer .time-unit .label {
			font-size: 9px;
			text-transform: uppercase;
		}

		@container (max-width: 300px) {
			#amasty-countdown-timer.amasty-product-countdown-timer :is(.countdown-position-right, .countdown-position-left) #timer .time-unit .label {
				font-size: 6px;
			}
		}
	`,
	holiday_special: `
		#timer .time-unit .number {
			align-items: center;
			border-radius: 999px !important;
			display: flex;
			height: 46px;
			width: 46px !important;
			justify-content: center;
			margin-bottom: 6px !important;
			min-width: 46px;
			padding: 0 !important;
			font-family: 'PT Serif Caption', sans-serif;
		}

		#amasty-countdown-timer .label {
			font-family: 'PT Mono', sans-serif;
			font-size: 11px;
		}

		#amasty-countdown-timer.amasty-timer-mobile:not(.amasty-product-countdown-timer) :is(.countdown-position-right, .countdown-position-left) #timer .time-unit .number {
			height: 32px;
			width: 32px !important;
			min-width: 32px;
			margin-bottom: 0 !important;
		}

		@container (max-width: 420px) {
			#amasty-countdown-timer.amasty-product-countdown-timer :is(.countdown-position-right, .countdown-position-left) #timer .time-unit .number {
				height: 32px;
				width: 32px !important;
				min-width: 32px;
				margin-bottom: 0 !important;
			}
		}

		@container (max-width: 300px) {
			#amasty-countdown-timer.amasty-product-countdown-timer :is(.countdown-position-right, .countdown-position-left) #timer .time-unit .number {
				height: 24px;
				width: 24px !important;
				min-width: 24px;
			}

			#amasty-countdown-timer.amasty-product-countdown-timer :is(.countdown-position-right, .countdown-position-left) #timer .time-unit .label {
				font-size: 6px;
			}
		}
	`,
	flash_sale: `
		#timer .time-unit .number {
			border: 2px solid #FFF083;
			border-radius: 3px !important;
			margin-bottom: 6px !important;
		}
	`,
	free_shipping: `
		#timer {
			background: #FFFFFF;
			border-radius: 24px;
			padding: 10px 22px;
		}

		#timer .time-unit .number {
			border-radius: 0 !important;
			margin-bottom: 0 !important;
			padding: 0 6px !important;
			font-weight: 900;
		}

		#amasty-countdown-timer.amasty-timer-mobile:not(.amasty-product-countdown-timer) :is(.countdown-position-right, .countdown-position-left) #timer {
			border-radius: 16px;
			padding: 6px 10px;
		}

		@container (max-width: 420px) {
			#amasty-countdown-timer.amasty-product-countdown-timer :is(.countdown-position-right, .countdown-position-left) #timer {
				border-radius: 16px;
				padding: 6px 10px;
			}
		}

		@container (max-width: 300px) {
			#amasty-countdown-timer.amasty-product-countdown-timer :is(.countdown-position-right, .countdown-position-left) #timer {
				border-radius: 12px;
				padding: 4px 6px;
			}
		}
	`,
};

/** Fonts the template styles refer to on top of the selected font. */
const TEMPLATE_FONT_URLS: Partial<Record<TimerTemplateId, string[]>> = {
	holiday_special: [
		'https://fonts.googleapis.com/css2?family=PT+Mono&display=swap',
		'https://fonts.googleapis.com/css2?family=PT+Serif+Caption&display=swap',
	],
};

export function findTimerTemplate(templateId: unknown): TimerTemplate | undefined {
	return TIMER_TEMPLATES.find((template) => template.id === templateId);
}

export function getTemplateStyles(templateId: string | null): string {
	return findTimerTemplate(templateId) ? (TEMPLATE_STYLES[templateId as TimerTemplateId] ?? '') : '';
}

export function getTemplateFontUrls(templateId: string | null): string[] {
	return findTimerTemplate(templateId) ? (TEMPLATE_FONT_URLS[templateId as TimerTemplateId] ?? []) : [];
}

/** Stored the same way as an uploaded background: a path that the API serves under `/api`. */
export function getTemplateBackgroundPath(template: TimerTemplate): string | null {
	return template.background ? `${TEMPLATE_BACKGROUND_DIRECTORY}${template.background.fileName}` : null;
}

/**
 * Replaces the look of the timer with the template's, background included; the counter position is a
 * separate setting and is passed through. A custom mobile view is dropped too, so both views show the new
 * template. An unknown template id (a stale or hand-edited link) falls back to the standard template.
 */
export function applyTimerTemplate(
	config: ConfigData,
	templateId: unknown,
	countdownPosition: CountdownPosition,
): ConfigData {
	const template = findTimerTemplate(templateId) ?? (findTimerTemplate(DEFAULT_TEMPLATE_ID) as TimerTemplate);

	return {
		...config,
		...template.patch,
		template_id: template.id,
		countdown_position: countdownPosition,
		background_image: getTemplateBackgroundPath(template),
		original_image_name: template.background?.displayName ?? null,
		mobile_use_desktop_settings: true,
		mobile_appearance: null,
	};
}
